/**
 * Scroll-scrubbed translateY for columns inside moving-columns (section top vs viewport).
 */

import { isDesktopBreakpoint } from '@theme/utilities';

/**
 * @param {DOMRect} rect
 * @param {number} vh
 */
function getSectionTopViewportMetrics(rect, vh) {
  if (vh <= 0) {
    return {
      topFromViewportTopPx: rect.top,
      topFromViewportTopNorm: 0,
    };
  }

  return {
    /** px from viewport top to block top */
    topFromViewportTopPx: rect.top,
    /** rect.top / vh */
    topFromViewportTopNorm: rect.top / vh,
  };
}

/**
 * @param {number} n
 * @param {number} min
 * @param {number} max
 */
function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

class MovingColumns extends HTMLElement {
  /** @type {(() => void) | null} */
  #onScroll = null;

  /** @type {(() => void) | null} */
  #onResize = null;

  /** @type {number | null} */
  #rafId = null;

  /** @type {number | null} */
  #stabilizeRaf = null;

  /** @type {boolean} */
  #stabilized = false;

  connectedCallback() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) {
      return;
    }

    this.#stabilized = false;
    this.#stabilizeRaf = requestAnimationFrame(() => {
      this.#stabilizeRaf = requestAnimationFrame(() => {
        this.#stabilizeRaf = null;
        this.#stabilized = true;
        this.#applyTransforms();
      });
    });

    this.#onScroll = () => {
      this.#scheduleApply();
    };
    this.#onResize = () => {
      this.#scheduleApply();
    };

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize);
    this.#scheduleApply();
  }

  disconnectedCallback() {
    if (this.#stabilizeRaf !== null) {
      cancelAnimationFrame(this.#stabilizeRaf);
      this.#stabilizeRaf = null;
    }

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }

    if (this.#onScroll) {
      window.removeEventListener('scroll', this.#onScroll);
      this.#onScroll = null;
    }

    if (this.#onResize) {
      window.removeEventListener('resize', this.#onResize);
      this.#onResize = null;
    }
  }

  #scheduleApply() {
    if (this.#rafId !== null) {
      return;
    }

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#applyTransforms();
    });
  }

  /** Reset column transforms (e.g. when viewport is mobile). */
  #clearColumnTransforms() {
    const columns = this.querySelectorAll('.moving-columns__column');
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      if (col instanceof HTMLElement) {
        col.style.transform = '';
      }
    }
  }

  #applyTransforms() {
    if (!this.#stabilized) {
      return;
    }

    if (!isDesktopBreakpoint()) {
      this.#clearColumnTransforms();
      return;
    }

    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    getSectionTopViewportMetrics(rect, vh);

    const rawStart = parseFloat(this.dataset.staggerStartViewportPercent || '85');
    const rawEnd = parseFloat(this.dataset.staggerEndViewportPercent || '35');
    const startPct = Number.isFinite(rawStart) ? clamp(rawStart, 0, 100) : 85;
    const endPct = Number.isFinite(rawEnd) ? clamp(rawEnd, 0, 100) : 35;

    const startTopPx = (startPct / 100) * vh;
    const endTopPx = (endPct / 100) * vh;
    const span = startTopPx - endTopPx;

    /** @type {number} */
    let progress;
    if (span <= 0) {
      progress = rect.top <= endTopPx ? 1 : 0;
    } else {
      progress = clamp((startTopPx - rect.top) / span, 0, 1);
    }

    // data-offset-column-1 etc. cannot be read via dataset.* (hyphen+digit breaks camelCase mapping).
    const o1 = parseFloat(this.getAttribute('data-offset-column-1') || '48');
    const o2 = parseFloat(this.getAttribute('data-offset-column-2') || '0');
    const o3 = parseFloat(this.getAttribute('data-offset-column-3') || '96');

    const offsets = [
      Number.isFinite(o1) ? o1 : 0,
      Number.isFinite(o2) ? o2 : 0,
      Number.isFinite(o3) ? o3 : 0,
    ];

    const columns = this.querySelectorAll('.moving-columns__column');

    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      if (!(col instanceof HTMLElement)) {
        continue;
      }

      if (i < 3) {
        const offset = offsets[i] ?? 0;
        const y = offset * progress;
        col.style.transform = `translateY(${y}px)`;
      } else {
        col.style.transform = '';
      }
    }
  }
}

if (!customElements.get('moving-columns')) {
  customElements.define('moving-columns', MovingColumns);
}
